import dayjs from 'dayjs';
import type { ExecLine, ExecRecord } from '../AppTuVan/execLog';
import type { ServerVisit } from '../AppTuVan/receptionServer';
import { machineOptions } from './ktCards';
import { sessionRows, type SessionRow } from './ktCounter';
import type { Bill, BillExec, BillLine, KtState, Team } from './ktLogic';

/**
 * Nối khâu thực hiện sang Kế toán: chỉ đọc, không ghi ngược. Có 2 nguồn:
 * - Lượt của Lễ tân trên server: một bác sĩ, một KTV, một phòng cho cả lượt, giờ bắt đầu và xong.
 * - Bản ghi Lễ tân ghi theo từng dịch vụ (đang lưu localStorage): đủ ekip, mã máy, số shot. Có bản ghi thì ưu tiên bản ghi.
 */

const digits = (s?: string | null) => (s || '').replace(/\D/g, '');
const ts = (s?: string | null) => (s ? dayjs(String(s).replace(' ', 'T')).valueOf() : undefined);
const dayOf = (v: ServerVisit) => (v.visit_date || v.check_in_at || '').slice(0, 10);
const hasExec = (v: ServerVisit) => !!(v.tech || v.doctor) && v.status !== 'cancelled';
/** Bill bán dịch vụ của POS, sổ chưa khóa: mới nhận dữ liệu thực hiện, để tua của ngày đã khóa không tự đổi */
const receives = (s: Pick<KtState, 'locks'>, b: Bill) => !!b.posOrderId && b.status === 'valid' && b.kind === 'service' && !s.locks[b.date];

export function execFromVisit(v: ServerVisit, by: BillExec['by']): BillExec {
  return { visitId: v.id, visitCode: v.code, by, doctor: v.doctor?.name || '', tech: v.tech?.name || '', room: v.exec_room?.name || '', at: ts(v.exec_at), doneAt: ts(v.exec_done_at) };
}

/** Lượt của một bill: theo lịch hẹn của đơn; đơn không có lịch hẹn thì theo số điện thoại trong cùng ngày, phải đúng một lượt mới nhận */
export function visitOfBill(b: Pick<Bill, 'appointmentId' | 'phone' | 'date'>, visits: ServerVisit[]): { visit: ServerVisit; by: BillExec['by'] } | null {
  const done = visits.filter(hasExec);
  if (b.appointmentId) {
    const visit = done.find(v => v.appointment?.id === b.appointmentId);
    return visit ? { visit, by: 'appointment' } : null;
  }
  const phone = digits(b.phone);
  if (!phone) return null;
  const same = done.filter(v => !v.appointment && digits(v.customer?.phone) === phone && dayOf(v) === b.date);
  return same.length === 1 ? { visit: same[0], by: 'phone' } : null;
}

/**
 * Ghi người thực hiện theo lượt vào bill POS.
 * Chỉ cập nhật ekip khi Lễ tân đổi người, nên phần Kế toán nội bộ đã gán bổ sung không bị ghi đè ở lần đọc sau.
 * Bill đã có bản ghi theo dịch vụ thì chỉ nhận giờ từ lượt.
 */
export function applyExecVisits(s: KtState, visits: ServerVisit[], now: number): KtState {
  let changed = false;
  const bills = s.bills.map(b => {
    if (!receives(s, b)) return b;
    const hit = visitOfBill(b, visits);
    if (!hit) return b;
    const fromVisit = execFromVisit(hit.visit, hit.by);
    const exec = b.exec?.logAt ? { ...b.exec, at: fromVisit.at, doneAt: fromVisit.doneAt } : fromVisit;
    if (JSON.stringify(exec) === JSON.stringify(b.exec)) return b;
    changed = true;
    const sameStaff = b.exec?.doctor === exec.doctor && b.exec?.tech === exec.tech;
    if (sameStaff) return { ...b, exec };
    // Lần đầu nối lượt chỉ điền ô trống: Kế toán nội bộ có thể đã gán người trước khi đọc lượt. Từ lần sau, Lễ tân đổi người thì cập nhật
    const fill = (cur: string | undefined, next: string) => (b.exec ? next || cur : cur || next);
    const team = { ...b.team, doctor: fill(b.team?.doctor, exec.doctor), ktv: fill(b.team?.ktv, exec.tech) };
    if (!team.doctor) delete team.doctor;
    if (!team.ktv) delete team.ktv;
    if (team.doctor === b.team?.doctor && team.ktv === b.team?.ktv) return { ...b, exec };
    return { ...b, exec, team, log: [...b.log, { at: now, by: 'Lễ tân', action: `Nhận người thực hiện từ lượt ${exec.visitCode}`, changes: [{ field: 'Ekip', from: Object.values(b.team || {}).filter(Boolean).join(', ') || '(trống)', to: Object.values(team).filter(Boolean).join(', ') }] }] };
  });
  return changed ? { ...s, bills } : s;
}

// ===================== Bản ghi theo từng dịch vụ =====================

export function recordOfBill(b: Pick<Bill, 'appointmentId' | 'phone' | 'date'>, records: ExecRecord[]): { record: ExecRecord; by: BillExec['by'] } | null {
  if (b.appointmentId) {
    const record = records.find(r => r.appointmentId === b.appointmentId);
    return record ? { record, by: 'appointment' } : null;
  }
  const phone = digits(b.phone);
  if (!phone) return null;
  const same = records.filter(r => !r.appointmentId && digits(r.phone) === phone && r.date === b.date);
  return same.length === 1 ? { record: same[0], by: 'phone' } : null;
}

const nameKey = (s: string) => s.trim().toLowerCase();
/** Dòng ghi của một dịch vụ trên bill: theo tên dịch vụ; bản ghi chỉ có một dòng thì áp cho cả bill */
const lineFor = (r: ExecRecord, name: string): ExecLine | undefined => r.lines.find(l => nameKey(l.service) === nameKey(name)) || (r.lines.length === 1 ? r.lines[0] : undefined);
const teamOf = (l: ExecLine): Team => ({ ...(l.doctor ? { doctor: l.doctor } : {}), ...(l.assistant ? { assistant: l.assistant } : {}), ...(l.nurse ? { nurse: l.nurse } : {}), ...(l.ktv ? { ktv: l.ktv } : {}) });

function applyRecord(b: Bill, record: ExecRecord, by: BillExec['by'], now: number): Bill {
  const lines: BillLine[] = b.lines.map(l => {
    const x = lineFor(record, l.name);
    return x ? { ...l, team: teamOf(x), machine: x.machine, shots: x.shots } : l;
  });
  const first = record.lines[0];
  const exec: BillExec = { visitId: record.serverId || 0, visitCode: record.visitCode, by, doctor: first?.doctor || '', tech: first?.ktv || '', room: record.room, at: record.at, doneAt: record.doneAt, logAt: record.updatedAt };
  return { ...b, lines, exec, log: [...b.log, { at: now, by: 'Lễ tân', action: `Nhận ekip, mã máy từ lượt ${record.visitCode}` }] };
}

/** Ghi bản ghi theo dịch vụ vào bill POS: bản ghi đổi mới nhận lại, nên phần Kế toán nội bộ đã gán không bị ghi đè */
export function applyExecLog(s: KtState, records: ExecRecord[], now: number): KtState {
  let changed = false;
  const bills = s.bills.map(b => {
    if (!receives(s, b)) return b;
    const hit = recordOfBill(b, records);
    if (!hit || b.exec?.logAt === hit.record.updatedAt) return b;
    changed = true;
    return applyRecord(b, hit.record, hit.by, now);
  });
  return changed ? { ...s, bills } : s;
}

// ===================== Đối soát ca =====================

export type ExecCheck = 'done' | 'no_machine' | 'running' | 'none';
export const EXEC_CHECK: Record<ExecCheck, { label: string; color: string }> = {
  done: { label: 'Đã xong', color: 'green' },
  no_machine: { label: 'Xong, thiếu mã máy', color: 'orange' },
  running: { label: 'Đang làm', color: 'blue' },
  none: { label: 'Chưa có dữ liệu', color: 'gold' },
};

export interface ExecRow extends SessionRow { exec?: BillExec; check: ExecCheck }

export const execCheckOf = (exec: BillExec | undefined, needMachine = false, machine = ''): ExecCheck => {
  if (!exec) return 'none';
  if (!exec.doneAt) return 'running';
  return needMachine && !machine ? 'no_machine' : 'done';
};

/** Dịch vụ lẻ của một ngày kèm dữ liệu từ khâu thực hiện */
export function execRows(s: Pick<KtState, 'bills' | 'services'>, date: string): ExecRow[] {
  return sessionRows(s, date).map(r => {
    const exec = s.bills.find(b => b.id === r.billId)?.exec;
    return { ...r, exec, check: execCheckOf(exec, machineOptions([r.group]).length > 0, r.machine) };
  });
}
